import React, { useMemo, useState } from "react";

import { Btn, ConfirmDialog, Icon, Menu, Stepper, Thumb, cx } from "../../catalogProductStudio/edit/StudioUi";
import {
  groupEntriesByProduct,
  lineQuantity,
  summarizeSelection,
  validateSelection,
} from "../state/printSelectionModel";
import { blockedReasonText } from "../PrintCenterUi";
import { describeVariant, fill, plural } from "../printCenterModel";

const issueText = (tt, issue) => {
  switch (issue.code) {
    case "new_stock_exceeds_outstanding":
      return fill(tt("issueNewStockTooHigh"), { available: issue.available });
    case "quantity_exceeds_on_hand":
      return fill(tt("issueAboveOnHand"), { quantity: issue.quantity, onHand: issue.onHand });
    case "unavailable":
      return tt("issueUnavailable");
    case "archived_product":
      return tt("issueArchived");
    default:
      return blockedReasonText(tt, issue.item);
  }
};

function TrayLine({ tt, entry, issues, onSetQuantities, onRemove }) {
  const { item } = entry;
  const outstanding = Math.max(0, Number(item.demand?.outstanding || 0));
  const showNewStock = outstanding > 0 || Number(entry.newStock) > 0;
  const quantity = lineQuantity(entry);

  return (
    <li className={cx("pc-tray-line", quantity <= 0 && "is-zero", issues.some((issue) => issue.blocking) && "is-blocked")}>
      <div className="pc-tray-line__head">
        <div className="pc-tray-line__identity">
          <strong>{describeVariant(item) || tt("variantFallback")}</strong>
          {item.kind === "unit" ? <span className="pc-unit-tag">{tt("unitTag")}</span> : null}
          <code>{item.barcode}</code>
        </div>
        <button type="button" className="pse-icon-btn pc-tray-line__remove" aria-label={fill(tt("removeLine"), { name: describeVariant(item) })} onClick={() => onRemove([item.key])}>
          <Icon name="x" size={16} />
        </button>
      </div>

      <div className="pc-tray-line__facts">
        <span>{fill(tt("trayOnHand"), { count: item.onHand })}</span>
        <span className={cx(outstanding > 0 && "pc-demand-text")}>{fill(tt("trayNeedsLabels"), { count: outstanding })}</span>
      </div>

      <div className="pc-tray-line__qty">
        {showNewStock ? (
          <label className="pc-qty-field is-new-stock">
            <span>{tt("qtyNewStock")}</span>
            <Stepper
              value={String(entry.newStock)}
              onChange={(value) => onSetQuantities({ key: item.key, newStock: value })}
              min={0}
              max={Math.max(outstanding, Number(entry.newStock) || 0)}
              ariaLabel={fill(tt("qtyNewStockFor"), { name: describeVariant(item) })}
              decreaseLabel={tt("decrease")}
              increaseLabel={tt("increase")}
            />
          </label>
        ) : null}
        <label className="pc-qty-field">
          <span>{tt("qtyExtra")}</span>
          <Stepper
            value={String(entry.extra)}
            onChange={(value) => onSetQuantities({ key: item.key, extra: value })}
            min={0}
            max={2000}
            ariaLabel={fill(tt("qtyExtraFor"), { name: describeVariant(item) })}
            decreaseLabel={tt("decrease")}
            increaseLabel={tt("increase")}
          />
        </label>
        <span className="pc-qty-total">
          {quantity > 0 ? fill(tt("qtyPrint"), { count: quantity }) : tt("qtyZero")}
        </span>
      </div>

      {issues.length ? (
        <ul className="pc-tray-line__issues">
          {issues.map((issue) => (
            <li key={issue.code} className={issue.blocking ? "is-blocking" : "is-warning"}>
              <Icon name="alert" size={13} />
              {issueText(tt, issue)}
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function PrintTray({ tt, selection, slotsPerPage, onSetQuantities, onRemove, onClear, onBulk, onReview, isMobileOpen, onToggleMobile }) {
  const [confirmClear, setConfirmClear] = useState(false);
  const { entries, order } = selection;
  const summary = summarizeSelection(entries, order);
  const validation = useMemo(() => validateSelection(entries, order), [entries, order]);
  const groups = useMemo(() => groupEntriesByProduct(entries, order), [entries, order]);
  const issuesByKey = useMemo(() => {
    const map = new Map();
    validation.blocking.forEach((issue) => map.set(issue.key, [...(map.get(issue.key) || []), { ...issue, blocking: true }]));
    validation.warnings
      .filter((issue) => issue.key)
      .forEach((issue) => map.set(issue.key, [...(map.get(issue.key) || []), issue]));
    return map;
  }, [validation]);
  const pages = slotsPerPage ? Math.ceil(summary.labels / slotsPerPage) : 0;
  const canReview = summary.labels > 0 && !validation.blocking.length;

  return (
    <aside className={cx("pc-tray", isMobileOpen && "is-open")} aria-label={tt("trayTitle")}>
      <button type="button" className="pc-tray__mobile-bar" onClick={onToggleMobile} aria-expanded={Boolean(isMobileOpen)}>
        <span>
          <strong>{plural(tt, "lineCount", summary.lines)}</strong> · {plural(tt, "labelCount", summary.labels)}
        </span>
        <Icon name={isMobileOpen ? "chevronDown" : "arrowUpDown"} size={16} />
      </button>

      <header className="pc-tray__head">
        <div>
          <h2 className="pc-tray__title">
            <Icon name="printer" size={18} /> {tt("trayTitle")}
          </h2>
          <p className="pc-tray__summary">
            <strong>{plural(tt, "lineCount", summary.lines)}</strong>
            <span>·</span>
            <strong>{plural(tt, "labelCount", summary.labels)}</strong>
            {pages ? (
              <>
                <span>·</span>
                <span>{plural(tt, "pageEstimate", pages)}</span>
              </>
            ) : null}
          </p>
          {summary.labels ? (
            <p className="pc-tray__split pc-small">
              <span className="pc-demand-text">{fill(tt("traySplitNew"), { count: summary.newStock })}</span>
              <span className="pc-muted">{fill(tt("traySplitExtra"), { count: summary.extra })}</span>
            </p>
          ) : null}
        </div>
        {summary.lines ? (
          <Menu
            label={tt("trayActions")}
            buttonLabel={tt("trayActions")}
            items={[
              { label: tt("bulkAllOne"), onClick: () => onBulk("all_one") },
              { label: tt("bulkOnHand"), onClick: () => onBulk("on_hand") },
              { label: tt("bulkOutstanding"), onClick: () => onBulk("outstanding") },
              { label: tt("bulkRemoveZero"), onClick: () => onBulk("remove_zero"), disabled: !summary.zeroLines },
              { label: tt("bulkClear"), onClick: () => setConfirmClear(true), tone: "danger" },
            ]}
          />
        ) : null}
      </header>

      <div className="pc-tray__body">
        {!summary.lines ? (
          <div className="pc-tray__empty">
            <Icon name="printer" size={26} />
            <strong>{tt("trayEmptyTitle")}</strong>
            <span className="pc-muted pc-small">{tt("trayEmptyCopy")}</span>
          </div>
        ) : (
          groups.map((group) => (
            <section key={group.productId} className="pc-tray-group">
              <header className="pc-tray-group__head">
                <Thumb src={group.imageUrl} size={34} fallbackLabel={group.productTitle} />
                <div>
                  <strong>{group.productTitle}</strong>
                  <span className="pc-muted pc-small">{[group.baseSku, group.brandName].filter(Boolean).join(" · ")}</span>
                </div>
              </header>
              <ul className="pc-tray-lines">
                {group.entries.map((entry) => (
                  <TrayLine
                    key={entry.item.key}
                    tt={tt}
                    entry={entry}
                    issues={issuesByKey.get(entry.item.key) || []}
                    onSetQuantities={onSetQuantities}
                    onRemove={onRemove}
                  />
                ))}
              </ul>
            </section>
          ))
        )}
      </div>

      {summary.lines ? (
        <footer className="pc-tray__foot">
          {validation.blocking.length ? (
            <p className="pc-tray__blocking">
              <Icon name="alert" size={14} /> {plural(tt, "fixBeforePrinting", validation.blocking.length)}
            </p>
          ) : summary.zeroLines ? (
            <p className="pc-tray__note pc-small pc-muted">{plural(tt, "zeroLinesNote", summary.zeroLines)}</p>
          ) : null}
          <Btn variant="primary" icon="printer" block disabled={!canReview} onClick={onReview}>
            {fill(tt("reviewAndPrint"), { count: summary.labels })}
          </Btn>
        </footer>
      ) : null}

      <ConfirmDialog
        isOpen={confirmClear}
        title={tt("clearConfirmTitle")}
        confirmLabel={tt("clearConfirm")}
        cancelLabel={tt("cancel")}
        tone="danger"
        onConfirm={() => {
          setConfirmClear(false);
          onClear();
        }}
        onCancel={() => setConfirmClear(false)}
      >
        {plural(tt, "clearConfirmCopy", summary.labels)}
      </ConfirmDialog>
    </aside>
  );
}

export default PrintTray;
